/**
 * Caret geometry inside a textarea, for ↑/↓ at block edges. A hidden mirror element with the
 * textarea's text metrics reports which visual line the caret is on, wrapped lines included.
 * Runs only on arrow keys, never while typing.
 */
const COPIED = [
  'boxSizing',
  'width',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'textTransform',
  'wordSpacing',
  'tabSize',
] as const

let mirror: HTMLDivElement | null = null

function caretTop(ta: HTMLTextAreaElement, pos: number): { top: number; height: number; line: number } {
  if (!mirror) {
    mirror = document.createElement('div')
    mirror.setAttribute('aria-hidden', 'true')
    Object.assign(mirror.style, {
      position: 'absolute',
      visibility: 'hidden',
      top: '0',
      left: '-9999px',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
    })
    document.body.appendChild(mirror)
  }
  const cs = getComputedStyle(ta)
  for (const p of COPIED) mirror.style[p] = cs[p]
  mirror.textContent = ta.value.slice(0, pos)
  const marker = document.createElement('span')
  marker.textContent = '​'
  mirror.appendChild(marker)
  const line = parseFloat(cs.lineHeight) || 24
  const top = marker.offsetTop
  mirror.textContent = ta.value + '​'
  const height = mirror.scrollHeight
  return { top, height, line }
}

/** True if the caret sits on the first (up) or last (down) visual line of the textarea. */
export function caretOnEdgeLine(ta: HTMLTextAreaElement, up: boolean): boolean {
  const pos = up ? ta.selectionStart : ta.selectionEnd
  if (up && pos === 0) return true
  if (!up && pos === ta.value.length) return true
  const { top, height, line } = caretTop(ta, pos)
  const pad = parseFloat(getComputedStyle(ta).paddingTop) || 0
  return up ? top - pad < line * 0.5 : top + line * 1.5 > height
}

/** Caret column within its logical line (for keeping the column across blocks). */
export function caretColumn(ta: HTMLTextAreaElement): number {
  const pos = ta.selectionStart
  return pos - (ta.value.lastIndexOf('\n', pos - 1) + 1)
}

/** Viewport coordinates of the caret position `pos` (for popovers anchored at the caret). */
export function caretCoords(ta: HTMLTextAreaElement, pos: number): { left: number; top: number; height: number } {
  caretTop(ta, pos)
  const marker = mirror!.ownerDocument.createElement('span')
  mirror!.textContent = ta.value.slice(0, pos)
  marker.textContent = '\u200b'
  mirror!.appendChild(marker)
  const r = ta.getBoundingClientRect()
  const cs = getComputedStyle(ta)
  return {
    left: r.left + marker.offsetLeft - ta.scrollLeft,
    top: r.top + marker.offsetTop - ta.scrollTop,
    height: parseFloat(cs.lineHeight) || 24,
  }
}
